import React, { useState, useEffect, useMemo } from 'react';
import { ar, en } from '../utils/translations';
import { useToast, SuccessAnimation } from './Toast';
import { 
  FaBuilding, 
  FaPlus, 
  FaTh, 
  FaTable, 
  FaEdit, 
  FaTrash,
  FaDoorOpen,
  FaUserCheck,
  FaTools,
  FaKey,
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle,
  FaBullhorn,
  FaMoneyBillWave,
  FaClipboardCheck,
  FaTimes,
  FaExternalLinkAlt,
  FaCamera,
  FaImage,
  FaCheck,
  FaLayerGroup
} from 'react-icons/fa';
import InventoryWizard from './InventoryWizard';
import ProjectHub from './ProjectHub';
import UnitBlueprint from './UnitBlueprint';
import { resolveCoverImage } from '../utils/propertyCovers';
import { parseGoogleMapsLink } from '../utils/parseGoogleMapsLink';

// Use same API base as other components
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  return 'http://localhost:5001';
};
const API_BASE = getApiBaseUrl();

const Units = ({ language = 'ar', userRole }) => {
  const t = language === 'ar' ? ar : en;
  const toast = useToast();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards');
  const [statusFilter, setStatusFilter] = useState('');
  const [opsFilter, setOpsFilter] = useState(''); // needs_ad | pending_ad | live_ad | reserved | closed
  const [propertyFilter, setPropertyFilter] = useState('');
  const [sortBy, setSortBy] = useState('unit_asc'); // default: smallest → largest unit number
  const [showModal, setShowModal] = useState(false);
  const [showInventoryWizard, setShowInventoryWizard] = useState(false);
  const [mainView, setMainView] = useState('units');
  const [blueprintProperty, setBlueprintProperty] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState({
    unit_number: '',
    unit_type: 'apartment',
    project_id: '',
    property_id: '',
    floor_number: '',
    section: '',
    bedrooms: '',
    bathrooms: '',
    area_sqm: '',
    monthly_rent: '',
    status: 'vacant',
    notes: '',
    location_url: '',
    latitude: '',
    longitude: '',
  });

  // Quick templates for common unit configurations
  const unitTemplates = [
    { id: 'studio', label: language === 'ar' ? 'استوديو' : 'Studio', bedrooms: 0, bathrooms: 1, area: 45, icon: '🏠' },
    { id: '1br', label: language === 'ar' ? 'غرفة واحدة' : '1 Bedroom', bedrooms: 1, bathrooms: 1, area: 70, icon: '🛏️' },
    { id: '2br', label: language === 'ar' ? 'غرفتين' : '2 Bedrooms', bedrooms: 2, bathrooms: 2, area: 100, icon: '🏘️' },
    { id: '3br', label: language === 'ar' ? 'ثلاث غرف' : '3 Bedrooms', bedrooms: 3, bathrooms: 2, area: 150, icon: '🏡' }
  ];

  // Apply template
  const applyTemplate = (template) => {
    setFormData({
      ...formData,
      bedrooms: template.bedrooms,
      bathrooms: template.bathrooms,
      area_sqm: template.area
    });
    toast.success(language === 'ar' ? `تم تطبيق قالب ${template.label}` : `${template.label} template applied`);
  };

  // Listing modal state
  const [showListingModal, setShowListingModal] = useState(false);
  const [listingUnit, setListingUnit] = useState(null);
  const [listingFormData, setListingFormData] = useState({
    title: '',
    platform: 'حراج',
    price: '',
    sla_hours: 24
  });


  // Deposit modal state
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositUnit, setDepositUnit] = useState(null);
  const [depositFormData, setDepositFormData] = useState({
    client_name: '',
    client_phone: '',
    amount: '',
    payment_method: 'transfer',
    receipt_image: null,
    receipt_preview: null
  });
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  // Assignment modal state (for owners/managers)
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignUnit, setAssignUnit] = useState(null);
  const [marketingTeam, setMarketingTeam] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [assigningSLA, setAssigningSLA] = useState(1);
  const [submittingAssignment, setSubmittingAssignment] = useState(false);

  // Deposit cancellation state
  const [showCancelDepositModal, setShowCancelDepositModal] = useState(false);
  const [cancellingDeposit, setCancellingDeposit] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [submittingCancellation, setSubmittingCancellation] = useState(false);
  const [pendingCancellations, setPendingCancellations] = useState([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvingDeposit, setApprovingDeposit] = useState(null);
  const [viewingDeposit, setViewingDeposit] = useState(null);
  
  // View listing details modal
  const [showViewListingModal, setShowViewListingModal] = useState(false);
  const [viewingListing, setViewingListing] = useState(null);
  const [editingListingUrl, setEditingListingUrl] = useState('');
  const [savingListingUrl, setSavingListingUrl] = useState(false);
  const [showViewDepositModal, setShowViewDepositModal] = useState(false);
  const [refundData, setRefundData] = useState({
    refund_status: 'full',
    refund_amount: '',
    refund_method: 'transfer'
  });

  // Check if user is owner or manager
  const canAssign = userRole === 'owner' || userRole === 'manager';
  const canApprove = userRole === 'owner' || userRole === 'manager';
  // Marketing employees, managers, and owners can add deposits
  const canAddDeposit = userRole === 'owner' || userRole === 'manager' || userRole === 'employee';
  // All roles can cancel deposits (employees can request cancellation)
  const canCancelDeposit = true;

  // Unit listings data
  const [unitListings, setUnitListings] = useState({});
  
  // Marketing tasks for units (to show assignment indicator)
  const [unitTasks, setUnitTasks] = useState({});

  const statusMeta = {
    vacant: { 
      label: language === 'ar' ? 'متاحة' : 'Available', 
      color: '#dc2626', 
      bg: '#fef2f2',
      icon: FaDoorOpen 
    },
    rented: { 
      label: language === 'ar' ? 'مؤجرة' : 'Rented', 
      color: '#16a34a', 
      bg: '#f0fdf4',
      icon: FaUserCheck 
    },
    reserved: { 
      label: language === 'ar' ? 'محجوزة (قيد الإتمام)' : 'Reserved (in progress)', 
      color: '#ca8a04', 
      bg: '#fefce8',
      icon: FaKey 
    },
    maintenance: { 
      label: language === 'ar' ? 'صيانة' : 'Maintenance', 
      color: '#9333ea', 
      bg: '#faf5ff',
      icon: FaTools 
    },
    sold: {
      label: language === 'ar' ? 'مباعة' : 'Sold',
      color: '#475569',
      bg: '#f1f5f9',
      icon: FaCheckCircle
    }
  };

  const unitTypes = [
    { value: 'apartment', label: language === 'ar' ? 'شقة' : 'Apartment', icon: '🏢' },
    { value: 'villa', label: language === 'ar' ? 'فيلا' : 'Villa', icon: '🏡' },
    { value: 'office', label: language === 'ar' ? 'مكتب' : 'Office', icon: '🏢' },
    { value: 'shop', label: language === 'ar' ? 'محل' : 'Shop', icon: '🏪' },
    { value: 'warehouse', label: language === 'ar' ? 'مستودع' : 'Warehouse', icon: '🏭' },
    { value: 'land', label: language === 'ar' ? 'أرض' : 'Land', icon: '📐' }
  ];

  useEffect(() => {
    loadUnits();
    loadProjects();
    loadProperties();
    loadListings();
    loadUnitTasks();
    loadPendingCancellations();
  }, [statusFilter, propertyFilter]);

  useEffect(() => {
    try {
      const hint = sessionStorage.getItem('units_ops_filter');
      if (hint) {
        setOpsFilter(hint);
        sessionStorage.removeItem('units_ops_filter');
        if (hint === 'reserved') setStatusFilter('reserved');
      }
    } catch (_) { /* ignore */ }
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadUnits = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE}/api/units?`;
      if (statusFilter) url += `status=${statusFilter}&`;
      if (propertyFilter) url += `property_id=${propertyFilter}&`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch units');
      const data = await response.json();
      setUnits(data);
    } catch (error) {
      console.error('Error loading units:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/properties`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/projects`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  // Load listings for all units
  const loadListings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/listings`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        // Group listings by unit_id
        const grouped = {};
        data.forEach(listing => {
          if (!grouped[listing.unit_id]) {
            grouped[listing.unit_id] = [];
          }
          grouped[listing.unit_id].push(listing);
        });
        setUnitListings(grouped);
      }
    } catch (error) {
      console.error('Error loading listings:', error);
    }
  };

  // Load marketing tasks for units (owner/manager view)
  const loadUnitTasks = async () => {
    if (!canAssign) return; // Only load for owners/managers
    try {
      const response = await fetch(`${API_BASE}/api/marketing/unit-tasks`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        // Group by unit_id
        const grouped = {};
        data.forEach(task => {
          grouped[task.contact_id] = task;
        });
        setUnitTasks(grouped);
      }
    } catch (error) {
      console.error('Error loading unit tasks:', error);
    }
  };

  // Get unit's current listing status (a unit can have many ads — one per platform)
  const getUnitListingStatus = (unitId) => {
    const listings = unitListings[unitId] || [];
    const openStatuses = new Set(['ad_posted', 'active', 'pending']);
    const openListings = listings.filter((l) => openStatuses.has(l.status));
    const activeListing = listings.find(l => l.status === 'ad_posted' || l.status === 'active');
    const pendingListing = listings.find(l => l.status === 'pending');
    return {
      activeListing,
      pendingListing,
      openListings,
      hasListings: listings.length > 0,
    };
  };

  // Open listing modal
  const openListingModal = (unit) => {
    setListingUnit(unit);
    setListingFormData({
      title: `${unit.unit_type === 'apartment' ? (language === 'ar' ? 'شقة' : 'Apartment') : unit.unit_type} ${language === 'ar' ? 'للإيجار' : 'for rent'} - ${unit.unit_number}`,
      platform: 'عقار',
      price: unit.monthly_rent || '',
      sla_hours: 24,
      ad_license: '',
      platform_ad_number: '',
      external_url: '',
      has_brokerage_contract: false,
    });
    setShowListingModal(true);
  };

  // Submit listing
  const handleListingSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/api/listings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          unit_id: listingUnit.id,
          title: listingFormData.title,
          platform: listingFormData.platform,
          price: listingFormData.price || null,
          sla_hours: listingFormData.sla_hours,
          ad_license: listingFormData.ad_license || null,
          platform_ad_number: listingFormData.platform_ad_number || null,
          external_url: listingFormData.external_url || null,
          has_brokerage_contract: !!listingFormData.has_brokerage_contract,
        })
      });
      
      if (!response.ok) throw new Error('Failed to create listing');
      
      setShowListingModal(false);
      setListingUnit(null);
      loadListings();
      toast.success(language === 'ar' ? '✅ تم إنشاء الإعلان بنجاح! يجب نشره خلال 24 ساعة.' : '✅ Listing created! Post the ad within 24 hours.');
    } catch (error) {
      console.error('Error creating listing:', error);
      toast.error(language === 'ar' ? 'فشل في إنشاء الإعلان' : 'Failed to create listing');
    }
  };

  // Mark listing as posted
  const handleMarkAdPosted = async (listing) => {
    // Prompt for ad URL
    const adUrl = prompt(
      language === 'ar' 
        ? 'يرجى إدخال رابط الإعلان المنشور (مثال: https://haraj.com.sa/...)' 
        : 'Please enter the published ad URL (e.g., https://haraj.com.sa/...)'
    );
    
    // If user cancelled, don't proceed
    if (adUrl === null) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/listings/${listing.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status: 'ad_posted',
          ad_posted_at: new Date().toISOString(),
          external_url: adUrl && adUrl.trim() ? adUrl.trim() : null
        })
      });
      
      if (!response.ok) throw new Error('Failed to update listing');
      
      loadListings();
      toast.success(language === 'ar' 
        ? '✅ تم تحديث حالة الإعلان وحفظ الرابط!' 
        : '✅ Ad status updated and URL saved!');
    } catch (error) {
      console.error('Error updating listing:', error);
      toast.error(language === 'ar' ? 'فشل في تحديث الإعلان' : 'Failed to update listing');
    }
  };

  // Open view listing details modal
  const openViewListingModal = (listing) => {
    setViewingListing(listing);
    setEditingListingUrl(listing.external_url || '');
    setShowViewListingModal(true);
  };

  // Save listing URL
  const handleSaveListingUrl = async () => {
    if (!viewingListing) return;
    
    setSavingListingUrl(true);
    try {
      const response = await fetch(`${API_BASE}/api/listings/${viewingListing.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          external_url: editingListingUrl.trim() || null
        })
      });
      
      if (!response.ok) throw new Error('Failed to save URL');
      
      loadListings();
      toast.success(language === 'ar' ? 'تم حفظ الرابط!' : 'URL saved!');
      setViewingListing({ ...viewingListing, external_url: editingListingUrl.trim() });
    } catch (error) {
      console.error('Error saving URL:', error);
      toast.error(language === 'ar' ? 'فشل حفظ الرابط' : 'Failed to save URL');
    } finally {
      setSavingListingUrl(false);
    }
  };

  // Open deposit modal
  const openDepositModal = (unit) => {
    setDepositUnit(unit);
    setDepositFormData({
      client_name: '',
      client_phone: '',
      amount: '',
      payment_method: 'transfer',
      receipt_image: null,
      receipt_preview: null
    });
    setShowDepositModal(true);
  };

  // Handle receipt image selection
  const handleReceiptImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.warning(language === 'ar' ? 'يرجى اختيار صورة' : 'Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.warning(language === 'ar' ? 'حجم الصورة كبير جداً (الحد الأقصى 5MB)' : 'Image too large (max 5MB)');
        return;
      }
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setDepositFormData({
          ...depositFormData,
          receipt_image: file,
          receipt_preview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Payment methods
  const paymentMethods = [
    { value: 'transfer', label: language === 'ar' ? 'تحويل بنكي' : 'Bank Transfer' },
    { value: 'cash', label: language === 'ar' ? 'نقداً' : 'Cash' },
    { value: 'check', label: language === 'ar' ? 'شيك' : 'Check' },
    { value: 'card', label: language === 'ar' ? 'بطاقة' : 'Card' }
  ];

  // Load marketing team members
  const loadMarketingTeam = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/employees?department=marketing`, { 
        headers: getAuthHeaders() 
      });
      if (response.ok) {
        const data = await response.json();
        setMarketingTeam(data);
      }
    } catch (error) {
      console.error('Error loading marketing team:', error);
    }
  };

  // Open assignment modal
  const openAssignModal = (unit) => {
    setAssignUnit(unit);
    setSelectedEmployee('');
    setAssigningSLA(24);
    loadMarketingTeam();
    setShowAssignModal(true);
  };

  // Submit manual assignment
  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!assignUnit || !selectedEmployee) {
      toast.warning(language === 'ar' ? 'يرجى اختيار موظف' : 'Please select an employee');
      return;
    }
    
    setSubmittingAssignment(true);
    try {
      const response = await fetch(`${API_BASE}/api/marketing/assign-task`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          unit_id: assignUnit.id,
          assigned_to: selectedEmployee,
          sla_hours: assigningSLA
        })
      });
      
      if (!response.ok) throw new Error('Failed to assign task');
      
      const result = await response.json();
      setShowAssignModal(false);
      setAssignUnit(null);
      loadUnits();
      loadUnitTasks(); // Reload to show the new task indicator
      
      // Show success animation
      setSuccessMessage(language === 'ar' 
        ? `تم تعيين المهمة بنجاح! المهلة: ${assigningSLA} ساعة`
        : `Task assigned successfully! SLA: ${assigningSLA} hours`);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error(language === 'ar' ? 'فشل في تعيين المهمة' : 'Failed to assign task');
    } finally {
      setSubmittingAssignment(false);
    }
  };

  // Load pending cancellation requests (for owner/manager)
  const loadPendingCancellations = async () => {
    if (!canApprove) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/deposits/pending-cancellations`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPendingCancellations(data);
      }
    } catch (error) {
      console.error('Error loading pending cancellations:', error);
    }
  };

  // Request deposit cancellation (Step 1)
  const handleRequestCancellation = async (e) => {
    e.preventDefault();
    
    if (!cancellationReason || cancellationReason.trim().length < 10) {
      toast.warning(language === 'ar' 
        ? 'يرجى كتابة سبب الإلغاء (10 أحرف على الأقل)' 
        : 'Please enter cancellation reason (at least 10 characters)');
      return;
    }
    
    setSubmittingCancellation(true);
    try {
      const response = await fetch(`${API_BASE}/api/deposits/${cancellingDeposit.id}/request-cancel`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: cancellationReason.trim() })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast.success(language === 'ar' 
          ? '✅ تم إرسال طلب الإلغاء للموافقة' 
          : '✅ Cancellation request submitted for approval');
        setShowCancelDepositModal(false);
        setCancellingDeposit(null);
        setCancellationReason('');
        loadUnits();
        loadPendingCancellations();
      } else {
        toast.error(result.error || 'Failed to request cancellation');
      }
    } catch (error) {
      console.error('Error requesting cancellation:', error);
      toast.error(language === 'ar' ? 'فشل في إرسال الطلب' : 'Failed to submit request');
    } finally {
      setSubmittingCancellation(false);
    }
  };

  // Approve and execute cancellation (Step 2 - owner/manager only)
  const handleApproveCancellation = async (e) => {
    e.preventDefault();
    
    const confirmMsg = language === 'ar'
      ? `هل أنت متأكد من إلغاء العربون؟\n\nالوحدة: ${approvingDeposit.unit_number}\nالمبلغ: ${approvingDeposit.amount} ريال\n\nسيتم:\n• إلغاء العربون\n• إرجاع حالة الوحدة إلى "شاغرة"\n• إلغاء التذكيرات المعلقة`
      : `Are you sure you want to cancel this deposit?\n\nUnit: ${approvingDeposit.unit_number}\nAmount: ${approvingDeposit.amount} SAR\n\nThis will:\n• Cancel the deposit\n• Revert unit status to "vacant"\n• Cancel pending reminders`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setSubmittingCancellation(true);
    try {
      const response = await fetch(`${API_BASE}/api/deposits/${approvingDeposit.id}/approve-cancel`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          refund_status: refundData.refund_status,
          refund_amount: refundData.refund_status === 'partial' 
            ? parseFloat(refundData.refund_amount) 
            : (refundData.refund_status === 'full' ? approvingDeposit.amount : 0),
          refund_method: refundData.refund_method
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to approve cancellation' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      setSuccessMessage(language === 'ar' 
        ? 'تم إلغاء العربون بنجاح وتحديث حالة الوحدة' 
        : 'Deposit cancelled successfully and unit status updated');
      setShowSuccess(true);
      setShowApproveModal(false);
      setApprovingDeposit(null);
      setRefundData({ refund_status: 'full', refund_amount: '', refund_method: 'transfer' });
      loadUnits();
      loadPendingCancellations();
      toast.success(language === 'ar' ? '✅ تم إلغاء العربون بنجاح' : '✅ Deposit cancelled successfully');
    } catch (error) {
      console.error('Error approving cancellation:', error);
      toast.error(error.message || (language === 'ar' ? 'فشل في الموافقة على الإلغاء' : 'Failed to approve cancellation'));
    } finally {
      setSubmittingCancellation(false);
    }
  };

  // Open cancel deposit modal
  const openCancelDepositModal = (deposit, unit) => {
    setCancellingDeposit({ ...deposit, unit_number: unit.unit_number });
    setCancellationReason('');
    setShowCancelDepositModal(true);
  };

  // Open approve cancellation modal
  const openApproveModal = (deposit) => {
    setApprovingDeposit(deposit);
    setRefundData({
      refund_status: 'full',
      refund_amount: deposit.amount,
      refund_method: 'transfer'
    });
    setShowApproveModal(true);
  };

  // Submit deposit
  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    
    // Validate receipt image is provided
    if (!depositFormData.receipt_preview) {
      toast.warning(language === 'ar' ? 'يرجى إرفاق صورة الإيصال' : 'Please attach receipt image');
      return;
    }
    
    setUploadingReceipt(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/deposits`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          unit_id: depositUnit.id,
          client_name: depositFormData.client_name,
          client_phone: depositFormData.client_phone,
          amount: depositFormData.amount,
          payment_method: depositFormData.payment_method,
          receipt_image: depositFormData.receipt_preview // Send as base64
        })
      });
      
      if (!response.ok) throw new Error('Failed to record deposit');
      
      const result = await response.json();
      setShowDepositModal(false);
      setDepositUnit(null);
      loadUnits();
      loadListings();
      
      // Show success animation
      setSuccessMessage(language === 'ar' 
        ? 'تم تسجيل العربون! تم حجز الوحدة وإنشاء تذكير لإزالة الإعلان.'
        : 'Deposit recorded! Unit reserved and reminder created.');
      setShowSuccess(true);
    } catch (error) {
      console.error('Error recording deposit:', error);
      toast.error(language === 'ar' ? 'فشل في تسجيل العربون' : 'Failed to record deposit');
    } finally {
      setUploadingReceipt(false);
    }
  };

  // Platform options — one listing row per platform post; distinguish by platform_ad_number
  const platforms = [
    { value: 'عقار', label: language === 'ar' ? 'عقار' : 'Aqar' },
    { value: 'ديل', label: language === 'ar' ? 'ديل' : 'Deal' },
    { value: 'بيوت', label: language === 'ar' ? 'بيوت' : 'Bayut' },
    { value: 'حراج', label: language === 'ar' ? 'حراج' : 'Haraj' },
    { value: 'وصلت', label: language === 'ar' ? 'وصلت' : 'Wasalt' },
    { value: 'سكني', label: language === 'ar' ? 'سكني' : 'Sakani' },
    { value: 'إنستغرام', label: language === 'ar' ? 'إنستغرام' : 'Instagram' },
    { value: 'سناب شات', label: language === 'ar' ? 'سناب شات' : 'Snapchat' },
    { value: 'تيك توك', label: language === 'ar' ? 'تيك توك' : 'TikTok' },
    { value: 'تويتر / X', label: language === 'ar' ? 'تويتر / X' : 'Twitter / X' },
    { value: 'فيسبوك', label: language === 'ar' ? 'فيسبوك' : 'Facebook' },
    { value: 'واتساب', label: language === 'ar' ? 'واتساب' : 'WhatsApp' },
    { value: 'أخرى', label: language === 'ar' ? 'أخرى' : 'Other' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingUnit 
        ? `${API_BASE}/api/units/${editingUnit.id}`
        : `${API_BASE}/api/units`;

      const payload = {
        ...formData,
        metadata: {
          location_url: formData.location_url || null,
          latitude: formData.latitude !== '' ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude !== '' ? parseFloat(formData.longitude) : null,
        },
      };
      
      const response = await fetch(url, {
        method: editingUnit ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Failed to save unit');
      
      const wasEditing = !!editingUnit;
      setShowModal(false);
      setEditingUnit(null);
      resetForm();
      loadUnits();
      
      // Show success toast
      toast.success(language === 'ar' 
        ? (wasEditing ? '✅ تم تحديث الوحدة بنجاح' : '✅ تم حفظ الوحدة بنجاح')
        : (wasEditing ? '✅ Unit updated successfully' : '✅ Unit saved successfully'));
    } catch (error) {
      console.error('Error saving unit:', error);
      toast.error(language === 'ar' ? 'فشل في حفظ الوحدة' : 'Failed to save unit');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الوحدة؟' : 'Are you sure you want to delete this unit?')) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/units/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete unit');
      loadUnits();
      toast.success(language === 'ar' ? '✅ تم حذف الوحدة بنجاح' : '✅ Unit deleted successfully');
    } catch (error) {
      console.error('Error deleting unit:', error);
      toast.error(language === 'ar' ? 'فشل في حذف الوحدة' : 'Failed to delete unit');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/units/${id}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error('Failed to update status');
      loadUnits();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(language === 'ar' ? 'فشل في تحديث الحالة' : 'Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      unit_number: '',
      unit_type: 'apartment',
      project_id: '',
      property_id: '',
      floor_number: '',
      section: '',
      bedrooms: '',
      bathrooms: '',
      area_sqm: '',
      monthly_rent: '',
      status: 'vacant',
      notes: '',
      location_url: '',
      latitude: '',
      longitude: '',
    });
  };

  const applyMapsLink = (url) => {
    const parsed = parseGoogleMapsLink(url);
    if (!parsed) {
      setFormData((prev) => ({ ...prev, location_url: url }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      location_url: parsed.location_url || url,
      latitude: parsed.latitude != null ? String(parsed.latitude) : prev.latitude,
      longitude: parsed.longitude != null ? String(parsed.longitude) : prev.longitude,
    }));
    if (parsed.latitude != null) {
      toast.success(language === 'ar' ? 'تم قراءة الموقع من رابط خرائط جوجل' : 'Location read from Google Maps link');
    }
  };

  const openEditModal = (unit) => {
    setEditingUnit(unit);
    let meta = {};
    try {
      meta = typeof unit.metadata === 'string' ? JSON.parse(unit.metadata || '{}') : (unit.metadata || {});
    } catch { meta = {}; }
    setFormData({
      unit_number: unit.unit_number || '',
      unit_type: unit.unit_type || 'apartment',
      project_id: unit.project_id || '',
      property_id: unit.property_id || '',
      floor_number: unit.floor_number || '',
      section: unit.section || '',
      bedrooms: unit.bedrooms || '',
      bathrooms: unit.bathrooms || '',
      area_sqm: unit.area_sqm || '',
      monthly_rent: unit.monthly_rent || '',
      status: unit.status || 'vacant',
      notes: unit.notes || '',
      location_url: meta.location_url || '',
      latitude: meta.latitude != null ? String(meta.latitude) : '',
      longitude: meta.longitude != null ? String(meta.longitude) : '',
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingUnit(null);
    resetForm();
    setShowModal(true);
  };

  const getVacantDays = (vacantSince) => {
    if (!vacantSince) return null;
    const days = Math.floor((new Date() - new Date(vacantSince)) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getUnitTypeName = (type) => {
    const found = unitTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  // Summary stats
  const stats = {
    total: units.length,
    vacant: units.filter(u => u.status === 'vacant').length,
    rented: units.filter(u => u.status === 'rented').length,
    reserved: units.filter(u => u.status === 'reserved').length,
    maintenance: units.filter(u => u.status === 'maintenance').length,
    sold: units.filter(u => u.status === 'sold').length,
  };

  const unitMatchesOpsFilter = (unit) => {
    if (!opsFilter) return true;
    const { openListings, pendingListing, activeListing } = getUnitListingStatus(unit.id);
    if (opsFilter === 'needs_ad') {
      return unit.status === 'vacant' && openListings.length === 0;
    }
    if (opsFilter === 'pending_ad') return Boolean(pendingListing);
    if (opsFilter === 'live_ad') return Boolean(activeListing);
    if (opsFilter === 'reserved') return unit.status === 'reserved';
    if (opsFilter === 'closed') return unit.status === 'sold' || unit.status === 'rented';
    return true;
  };

  const displayUnits = useMemo(() => {
    const list = units.filter(unitMatchesOpsFilter);
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const byNumber = (a, b) => collator.compare(String(a.unit_number || ''), String(b.unit_number || ''));
    const rent = (u) => (u.monthly_rent != null && u.monthly_rent !== '' ? Number(u.monthly_rent) : null);
    const created = (u) => new Date(u.created_at || 0).getTime();

    const sorted = [...list];
    switch (sortBy) {
      case 'unit_desc':
        sorted.sort((a, b) => byNumber(b, a));
        break;
      case 'newest':
        sorted.sort((a, b) => created(b) - created(a));
        break;
      case 'oldest':
        sorted.sort((a, b) => created(a) - created(b));
        break;
      case 'rent_asc':
        sorted.sort((a, b) => {
          const ra = rent(a);
          const rb = rent(b);
          if (ra == null && rb == null) return byNumber(a, b);
          if (ra == null) return 1;
          if (rb == null) return -1;
          return ra - rb || byNumber(a, b);
        });
        break;
      case 'rent_desc':
        sorted.sort((a, b) => {
          const ra = rent(a);
          const rb = rent(b);
          if (ra == null && rb == null) return byNumber(a, b);
          if (ra == null) return 1;
          if (rb == null) return -1;
          return rb - ra || byNumber(a, b);
        });
        break;
      case 'unit_asc':
      default:
        sorted.sort(byNumber);
        break;
    }
    return sorted;
  }, [units, opsFilter, sortBy, unitListings]);

  const needsAdCount = units.filter((u) => {
    const { openListings } = getUnitListingStatus(u.id);
    return u.status === 'vacant' && openListings.length === 0;
  }).length;

  if (mainView === 'projects') {
    return (
      <div style={{ padding: '20px', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
        <ProjectHub language={language} onBack={() => setMainView('units')} />
      </div>
    );
  }

  if (mainView === 'blueprint' && blueprintProperty) {
    return (
      <div style={{ padding: '20px', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
        <UnitBlueprint
          language={language}
          property={blueprintProperty}
          units={units}
          onBack={() => {
            setMainView('units');
            setBlueprintProperty(null);
          }}
          onSelectUnit={(unit) => {
            openEditModal(unit);
            setMainView('units');
            setBlueprintProperty(null);
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        color: '#f97316'
      }}>
        <div>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaBuilding style={{ color: '#f97316' }} />
            {language === 'ar' ? 'إدارة الوحدات' : 'Units Management'}
          </h2>
          <button
            type="button"
            onClick={() => setMainView('projects')}
            style={{
              padding: '6px 12px',
              background: '#fff7ed',
              color: '#ea580c',
              border: '1px solid #fed7aa',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <FaLayerGroup />
            {language === 'ar' ? 'المشاريع' : 'Projects'}
          </button>
          <button
            type="button"
            onClick={() => {
              const prop =
                properties.find((p) => String(p.id) === String(propertyFilter)) || properties[0];
              if (!prop) {
                toast.error(
                  language === 'ar'
                    ? 'أنشئ عقاراً أولاً ثم افتح المخطط'
                    : 'Create a property first, then open the blueprint'
                );
                return;
              }
              setBlueprintProperty(prop);
              setMainView('blueprint');
            }}
            style={{
              padding: '6px 12px',
              background: '#fff',
              color: '#0f172a',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <FaTh />
            {language === 'ar' ? 'المخطط' : 'Blueprint'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {canAssign && (
            <button
              type="button"
              onClick={() => {
                setOpsFilter(opsFilter === 'needs_ad' ? '' : 'needs_ad');
                setViewMode('cards');
              }}
              style={{
                padding: '10px 14px',
                backgroundColor: opsFilter === 'needs_ad' ? '#fff7ed' : '#fff',
                color: '#c2410c',
                border: '1px solid #fdba74',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '600',
                fontSize: '0.875rem',
              }}
            >
              <FaBullhorn />
              {language === 'ar'
                ? `إنشاء إعلان${needsAdCount ? ` (${needsAdCount})` : ''}`
                : `Create ad${needsAdCount ? ` (${needsAdCount})` : ''}`}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowInventoryWizard(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '600',
            }}
          >
            <FaPlus />
            {language === 'ar' ? 'إضافة (معالج)' : 'Add (wizard)'}
          </button>
          <button
            type="button"
            onClick={openAddModal}
            style={{
              padding: '10px 16px',
              backgroundColor: '#fff',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '500',
              fontSize: '0.875rem',
            }}
          >
            {language === 'ar' ? 'إضافة سريعة' : 'Quick add'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '15px',
        marginBottom: '20px'
      }}>
        {Object.entries(statusMeta).map(([key, meta]) => {
          const IconComponent = meta.icon;
          return (
            <div
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              style={{
                padding: '15px',
                backgroundColor: statusFilter === key ? meta.bg : 'white',
                border: `2px solid ${statusFilter === key ? meta.color : '#e5e7eb'}`,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <IconComponent style={{ color: meta.color, fontSize: '24px' }} />
                <div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: meta.color }}>
                    {stats[key] || 0}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>{meta.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters & View Toggle */}
      <div style={{ 
        display: 'flex', 
        gap: '15px', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <select
          value={propertyFilter}
          onChange={(e) => setPropertyFilter(e.target.value)}
          style={{
            padding: '10px 15px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            minWidth: '200px'
          }}
        >
          <option value="">{language === 'ar' ? 'كل العقارات' : 'All Properties'}</option>
          {properties.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '10px 15px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            minWidth: '180px'
          }}
          aria-label={language === 'ar' ? 'ترتيب الوحدات' : 'Sort units'}
        >
          <option value="unit_asc">{language === 'ar' ? 'الرقم: من الأصغر للأكبر' : 'Number: smallest → largest'}</option>
          <option value="unit_desc">{language === 'ar' ? 'الرقم: من الأكبر للأصغر' : 'Number: largest → smallest'}</option>
          <option value="newest">{language === 'ar' ? 'الأحدث أولاً' : 'Newest first'}</option>
          <option value="oldest">{language === 'ar' ? 'الأقدم أولاً' : 'Oldest first'}</option>
          <option value="rent_asc">{language === 'ar' ? 'الإيجار: تصاعدي' : 'Rent: low → high'}</option>
          <option value="rent_desc">{language === 'ar' ? 'الإيجار: تنازلي' : 'Rent: high → low'}</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '5px' }}>
          <button
            onClick={() => setViewMode('cards')}
            style={{
              padding: '10px',
              backgroundColor: viewMode === 'cards' ? '#f97316' : '#f3f4f6',
              color: viewMode === 'cards' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            <FaTh />
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '10px',
              backgroundColor: viewMode === 'table' ? '#f97316' : '#f3f4f6',
              color: viewMode === 'table' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            <FaTable />
          </button>
        </div>
      </div>

      {opsFilter && (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 14px',
            background: '#fff7ed',
            border: '1px solid #fdba74',
            borderRadius: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            fontSize: 13,
            color: '#9a3412',
          }}
        >
          <span>
            {language === 'ar'
              ? `تصفية العمليات: ${opsFilter === 'needs_ad' ? 'وحدات تحتاج إعلان — اضغط «إنشاء إعلان» على البطاقة' : opsFilter}`
              : `Ops filter: ${opsFilter === 'needs_ad' ? 'units needing an ad — tap Create ad on a card' : opsFilter}`}
            {` · ${displayUnits.length}`}
          </span>
          <button
            type="button"
            onClick={() => setOpsFilter('')}
            style={{ border: 'none', background: 'transparent', color: '#c2410c', cursor: 'pointer', fontWeight: 600 }}
          >
            {language === 'ar' ? 'مسح التصفية' : 'Clear filter'}
          </button>
        </div>
      )}

      {/* Units Grid/Table */}
      {viewMode === 'cards' ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
          gap: '20px' 
        }}>
          {displayUnits.map(unit => {
            const status = statusMeta[unit.status] || statusMeta.vacant;
            const StatusIcon = status.icon;
            const vacantDays = getVacantDays(unit.vacant_since);
            const { activeListing, pendingListing, openListings, hasListings } = getUnitListingStatus(unit.id);
            
            return (
              <div
                key={unit.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: 0,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: `2px solid ${status.color}20`
                }}
              >
                <img
                  src={resolveCoverImage({
                    coverImageUrl: unit.cover_image_url,
                    unitType: unit.unit_type,
                  })}
                  alt=""
                  style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block', background: '#f5f5f4' }}
                />
                <div style={{ padding: '16px 20px 20px' }}>
                {/* Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '15px'
                }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>
                      {unit.unit_number}
                    </h3>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {getUnitTypeName(unit.unit_type)}
                      {unit.property_name && ` - ${unit.property_name}`}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 10px',
                    backgroundColor: status.bg,
                    color: status.color,
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    <StatusIcon />
                    {status.label}
                  </div>
                </div>

                {/* Details */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '10px',
                  marginBottom: '15px',
                  fontSize: '14px'
                }}>
                  {unit.bedrooms && (
                    <div>
                      <span style={{ color: '#9ca3af' }}>{language === 'ar' ? 'غرف:' : 'Beds:'}</span>
                      <span style={{ marginLeft: '5px', color: '#374151' }}>{unit.bedrooms}</span>
                    </div>
                  )}
                  {unit.area_sqm && (
                    <div>
                      <span style={{ color: '#9ca3af' }}>{language === 'ar' ? 'المساحة:' : 'Area:'}</span>
                      <span style={{ marginLeft: '5px', color: '#374151' }}>{unit.area_sqm} م²</span>
                    </div>
                  )}
                  {unit.monthly_rent && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span style={{ color: '#9ca3af' }}>{language === 'ar' ? 'الإيجار:' : 'Rent:'}</span>
                      <span style={{ marginLeft: '5px', color: '#16a34a', fontWeight: '600' }}>
                        {Number(unit.monthly_rent).toLocaleString()} {language === 'ar' ? 'ر.س/شهر' : 'SAR/mo'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Vacant warning */}
                {unit.status === 'vacant' && vacantDays !== null && vacantDays > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: vacantDays > 7 ? '#fef2f2' : '#fffbeb',
                    borderRadius: '8px',
                    marginBottom: '10px',
                    fontSize: '13px',
                    color: vacantDays > 7 ? '#dc2626' : '#d97706'
                  }}>
                    {vacantDays > 7 ? <FaExclamationTriangle /> : <FaClock />}
                    {language === 'ar' 
                      ? `شاغر منذ ${vacantDays} يوم`
                      : `Vacant for ${vacantDays} days`}
                  </div>
                )}

                {/* Open ads across platforms — each row is one platform post */}
                {openListings.length > 0 && (
                  <div style={{ marginBottom: 10, display: 'grid', gap: 6 }}>
                    {openListings.map((listing) => (
                      <div
                        key={listing.id}
                        onClick={() => openViewListingModal(listing)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 10px',
                          backgroundColor: listing.status === 'pending' ? '#fffbeb' : '#f0fdf4',
                          borderRadius: 8,
                          fontSize: 12,
                          color: listing.status === 'pending' ? '#92400e' : '#166534',
                          cursor: 'pointer',
                          border: `1px solid ${listing.status === 'pending' ? '#fcd34d' : '#bbf7d0'}`,
                        }}
                      >
                        <FaBullhorn style={{ flexShrink: 0 }} />
                        <span style={{ flex: 1, fontWeight: 600 }}>
                          {listing.platform || (language === 'ar' ? 'منصة' : 'Platform')}
                          {listing.platform_ad_number || listing.external_id
                            ? ` · #${listing.platform_ad_number || listing.external_id}`
                            : ''}
                        </span>
                        <span style={{ opacity: 0.85 }}>
                          {listing.status === 'pending'
                            ? (language === 'ar' ? 'بانتظار النشر' : 'Pending')
                            : (language === 'ar' ? 'منشور' : 'Live')}
                        </span>
                        {listing.status === 'pending' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAdPosted(listing);
                            }}
                            style={{
                              padding: '3px 8px',
                              background: '#16a34a',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              fontSize: 11,
                              cursor: 'pointer',
                            }}
                          >
                            {language === 'ar' ? 'تم النشر' : 'Posted'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Quick Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  marginBottom: '15px',
                  flexWrap: 'wrap'
                }}>
                  {/* Owner/marketing: create one ad per platform (same unit, different platform_ad_number) */}
                  {(unit.status === 'vacant' || unit.status === 'reserved') && (
                    <button
                      onClick={() => openListingModal(unit)}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        backgroundColor: '#fff7ed',
                        color: '#c2410c',
                        border: '1px solid #fdba74',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <FaBullhorn />
                      {openListings.length > 0
                        ? (language === 'ar' ? 'إعلان لمنصة أخرى' : 'Add another platform ad')
                        : (language === 'ar' ? 'إنشاء إعلان' : 'Create ad')}
                    </button>
                  )}

                  {/* Assign to Marketing - for vacant units without listing or task (owner/manager only) */}
                  {unit.status === 'vacant' && !pendingListing && !activeListing && !unitTasks[unit.id] && canAssign && (
                    <button
                      onClick={() => openAssignModal(unit)}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        border: '1px solid #fcd34d',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <FaBullhorn />
                      {language === 'ar' ? 'تعيين للتسويق' : 'Assign to Marketing'}
                    </button>
                  )}

                  {/* Record Deposit - show for units with active listing (marketing/manager/owner) */}
                  {activeListing && unit.status !== 'reserved' && canAddDeposit && (
                    <button
                      onClick={() => openDepositModal(unit)}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        backgroundColor: '#dbeafe',
                        color: '#2563eb',
                        border: '1px solid #93c5fd',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <FaMoneyBillWave />
                      {language === 'ar' ? 'تسجيل عربون' : 'Record Deposit'}
                    </button>
                  )}

                  {/* View Deposit - show for reserved units */}
                  {unit.status === 'reserved' && unit.active_deposit && (
                    <button
                      onClick={() => {
                        setViewingDeposit(unit.active_deposit);
                        setShowViewDepositModal(true);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        backgroundColor: '#f0f9ff',
                        color: '#0284c7',
                        border: '1px solid #7dd3fc',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <FaMoneyBillWave />
                      {language === 'ar' ? 'عرض العربون' : 'View Deposit'}
                    </button>
                  )}

                  {/* Cancel Deposit - show for reserved units (all roles) */}
                  {unit.status === 'reserved' && unit.active_deposit && canCancelDeposit && (
                    <button
                      onClick={() => openCancelDepositModal(unit.active_deposit, unit)}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        border: '1px solid #fca5a5',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <FaExclamationTriangle />
                      {language === 'ar' ? 'إلغاء العربون' : 'Cancel Deposit'}
                    </button>
                  )}
                </div>

                {/* Actions */}
                <div style={{ 
                  display: 'flex', 
                  gap: '10px',
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: '15px'
                }}>
                  <select
                    value={unit.status}
                    onChange={(e) => handleStatusChange(unit.id, e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '13px'
                    }}
                  >
                    {Object.entries(statusMeta).map(([key, meta]) => (
                      <option key={key} value={key}>{meta.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => openEditModal(unit)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(unit.id)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '12px 15px', textAlign: 'start', fontWeight: '600', color: '#374151' }}>
                  {language === 'ar' ? 'رقم الوحدة' : 'Unit #'}
                </th>
                <th style={{ padding: '12px 15px', textAlign: 'start', fontWeight: '600', color: '#374151' }}>
                  {language === 'ar' ? 'النوع' : 'Type'}
                </th>
                <th style={{ padding: '12px 15px', textAlign: 'start', fontWeight: '600', color: '#374151' }}>
                  {language === 'ar' ? 'العقار' : 'Property'}
                </th>
                <th style={{ padding: '12px 15px', textAlign: 'start', fontWeight: '600', color: '#374151' }}>
                  {language === 'ar' ? 'الإيجار' : 'Rent'}
                </th>
                <th style={{ padding: '12px 15px', textAlign: 'start', fontWeight: '600', color: '#374151' }}>
                  {language === 'ar' ? 'الحالة' : 'Status'}
                </th>
                <th style={{ padding: '12px 15px', textAlign: 'start', fontWeight: '600', color: '#374151' }}>
                  {language === 'ar' ? 'إجراءات' : 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayUnits.map(unit => {
                const status = statusMeta[unit.status] || statusMeta.vacant;
                const { openListings } = getUnitListingStatus(unit.id);
                return (
                  <tr key={unit.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px 15px', fontWeight: '500' }}>{unit.unit_number}</td>
                    <td style={{ padding: '12px 15px' }}>{getUnitTypeName(unit.unit_type)}</td>
                    <td style={{ padding: '12px 15px' }}>{unit.property_name || '-'}</td>
                    <td style={{ padding: '12px 15px' }}>
                      {unit.monthly_rent ? `${Number(unit.monthly_rent).toLocaleString()} ر.س` : '-'}
                    </td>
                    <td style={{ padding: '12px 15px' }}>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: status.bg,
                        color: status.color,
                        borderRadius: '20px',
                        fontSize: '13px'
                      }}>
                        {status.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 15px' }}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {(unit.status === 'vacant' || unit.status === 'reserved') && (
                          <button
                            onClick={() => openListingModal(unit)}
                            title={language === 'ar' ? 'إنشاء إعلان' : 'Create ad'}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#fff7ed',
                              color: '#c2410c',
                              border: '1px solid #fdba74',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            <FaBullhorn />
                            {openListings.length > 0 ? '+' : ''}
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(unit)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(unit.id)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#fef2f2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {units.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <FaBuilding style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '15px' }} />
          <h3 style={{ color: '#6b7280', margin: '0 0 10px 0' }}>
            {language === 'ar' ? 'لا توجد وحدات' : 'No units yet'}
          </h3>
          <p style={{ color: '#9ca3af', margin: 0 }}>
            {language === 'ar' ? 'أضف وحدتك الأولى للبدء' : 'Add your first unit to get started'}
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '650px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{ 
              padding: '24px 28px',
              borderBottom: '1px solid #f3f4f6',
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: 'linear-gradient(to bottom, #ffffff, #fefefe)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaDoorOpen style={{ color: '#f97316', fontSize: '18px' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1f2937', fontWeight: '600' }}>
                  {editingUnit 
                    ? (language === 'ar' ? 'تعديل الوحدة' : 'Edit Unit')
                    : (language === 'ar' ? 'إضافة وحدة جديدة' : 'Add New Unit')}
                </h3>
              </div>
              <button
                onClick={() => { setShowModal(false); setEditingUnit(null); }}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#6b7280',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fee2e2';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '28px 28px 24px' }}>
              {/* Basic Information Section */}
              <div style={{ 
                marginBottom: '28px',
                paddingBottom: '24px',
                borderBottom: '1px solid #f3f4f6'
              }}>
                <div style={{ 
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '16px'
                }}>
                  {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
                </div>

                {/* Unit Number */}
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '10px', 
                    fontWeight: '600', 
                    color: '#1f2937',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    {language === 'ar' ? 'رقم الوحدة' : 'Unit Number'}
                    <span style={{ 
                      color: '#ef4444',
                      fontSize: '0.85rem',
                      background: '#fee2e2',
                      padding: '1px 6px',
                      borderRadius: '4px'
                    }}>
                      *
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.unit_number}
                    onChange={(e) => setFormData({...formData, unit_number: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '16px 18px',
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      boxSizing: 'border-box',
                      fontSize: '1.05rem',
                      background: '#fafafa',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f97316';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.background = '#fafafa';
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder={language === 'ar' ? 'مثال: A101' : 'e.g. A101'}
                    autoFocus
                  />
                </div>

                {/* Unit Type - Visual Selector */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '12px', 
                    fontWeight: '600', 
                    color: '#1f2937',
                    fontSize: '0.95rem'
                  }}>
                    {language === 'ar' ? 'نوع الوحدة' : 'Unit Type'}
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                    {unitTypes.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({...formData, unit_type: type.value})}
                        style={{
                          padding: '12px 8px',
                          border: formData.unit_type === type.value 
                            ? '2px solid #f97316' 
                            : '2px solid #e5e7eb',
                          borderRadius: '10px',
                          background: formData.unit_type === type.value 
                            ? 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)' 
                            : 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (formData.unit_type !== type.value) {
                            e.currentTarget.style.borderColor = '#d1d5db';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (formData.unit_type !== type.value) {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {formData.unit_type === type.value && (
                          <div style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#f97316',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <FaCheck style={{ color: 'white', fontSize: '9px' }} />
                          </div>
                        )}
                        <span style={{ fontSize: '18px' }}>{type.icon || '🏢'}</span>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          color: formData.unit_type === type.value ? '#f97316' : '#6b7280',
                          textAlign: 'center'
                        }}>
                          {type.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Templates */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <label style={{ 
                      fontWeight: '600', 
                      color: '#1f2937',
                      fontSize: '0.95rem'
                    }}>
                      {language === 'ar' ? 'قوالب سريعة' : 'Quick Templates'}
                    </label>
                    <span style={{ 
                      fontSize: '0.7rem', 
                      color: '#9ca3af',
                      background: '#f3f4f6',
                      padding: '2px 8px',
                      borderRadius: '6px'
                    }}>
                      {language === 'ar' ? 'ملء تلقائي' : 'Auto-fill'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {unitTemplates.map(template => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => applyTemplate(template)}
                        style={{
                          padding: '8px 14px',
                          background: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          color: '#374151',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#fff7ed';
                          e.currentTarget.style.borderColor = '#fed7aa';
                          e.currentTarget.style.color = '#f97316';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.color = '#374151';
                        }}
                      >
                        <span>{template.icon}</span>
                        {template.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Project & Property */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px', alignItems: 'end' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#6b7280'
                    }}>
                      {language === 'ar' ? 'المشروع' : 'Project'}
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: '4px' }}>
                        ({language === 'ar' ? 'اختياري' : 'optional'})
                      </span>
                    </label>
                    <select
                      value={formData.project_id}
                      onChange={(e) => setFormData({...formData, project_id: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        fontSize: '0.95rem',
                        background: '#fafafa',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f97316';
                        e.target.style.background = 'white';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.background = '#fafafa';
                      }}
                    >
                      <option value="">{language === 'ar' ? 'بدون مشروع' : 'No Project'}</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#6b7280'
                    }}>
                      {language === 'ar' ? 'العقار' : 'Property'}
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: '4px' }}>
                        ({language === 'ar' ? 'اختياري' : 'optional'})
                      </span>
                    </label>
                    <select
                      value={formData.property_id}
                      onChange={(e) => setFormData({...formData, property_id: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        fontSize: '0.95rem',
                        background: '#fafafa',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f97316';
                        e.target.style.background = 'white';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.background = '#fafafa';
                      }}
                    >
                      <option value="">{language === 'ar' ? 'بدون عقار' : 'No Property'}</option>
                      {properties
                        .filter(p => !formData.project_id || p.project_id === parseInt(formData.project_id))
                        .map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Unit Details Section */}
              <div style={{ marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <div style={{ 
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {language === 'ar' ? 'تفاصيل الوحدة' : 'Unit Details'}
                  </div>
                  
                  {/* Live Summary Preview */}
                  {(formData.bedrooms || formData.bathrooms || formData.area_sqm) && (
                    <div style={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'center',
                      padding: '6px 12px',
                      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#166534'
                    }}>
                      {formData.bedrooms > 0 && <span>🛏️ {formData.bedrooms}</span>}
                      {formData.bathrooms > 0 && <span>🚿 {formData.bathrooms}</span>}
                      {formData.area_sqm > 0 && <span>📐 {formData.area_sqm}م²</span>}
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '18px' }}>
                  <div>
                    <label style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#6b7280'
                    }}>
                      <span style={{ fontSize: '14px' }}>🏢</span>
                      {language === 'ar' ? 'الطابق' : 'Floor'}
                    </label>
                    <input
                      type="number"
                      value={formData.floor_number}
                      onChange={(e) => setFormData({...formData, floor_number: e.target.value})}
                      placeholder="3"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        background: '#fafafa',
                        boxSizing: 'border-box',
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f97316';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.background = '#fafafa';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#6b7280'
                    }}>
                      <span style={{ fontSize: '14px' }}>▣</span>
                      {language === 'ar' ? 'القسم / الجناح' : 'Section / wing'}
                    </label>
                    <input
                      type="text"
                      value={formData.section || ''}
                      onChange={(e) => setFormData({...formData, section: e.target.value})}
                      placeholder={language === 'ar' ? 'أ / شرق / B' : 'A / East / B'}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        background: '#fafafa',
                        boxSizing: 'border-box',
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f97316';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.background = '#fafafa';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#6b7280'
                    }}>
                      <span style={{ fontSize: '14px' }}>🛏️</span>
                      {language === 'ar' ? 'غرف النوم' : 'Bedrooms'}
                    </label>
                    <input
                      type="number"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                      placeholder="2"
                      min="0"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        background: '#fafafa',
                        boxSizing: 'border-box',
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f97316';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.background = '#fafafa';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#6b7280'
                    }}>
                      <span style={{ fontSize: '14px' }}>🚿</span>
                      {language === 'ar' ? 'الحمامات' : 'Bathrooms'}
                    </label>
                    <input
                      type="number"
                      value={formData.bathrooms}
                      onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                      placeholder="2"
                      min="0"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        background: '#fafafa',
                        boxSizing: 'border-box',
                        fontSize: '0.95rem',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f97316';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.background = '#fafafa';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
                  <div>
                    <label style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#6b7280'
                    }}>
                      <span style={{ fontSize: '14px' }}>📐</span>
                      {language === 'ar' ? 'المساحة (م²)' : 'Area (sqm)'}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        value={formData.area_sqm}
                        onChange={(e) => setFormData({...formData, area_sqm: e.target.value})}
                        placeholder="120"
                        min="0"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          paddingRight: '40px',
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          background: '#fafafa',
                          boxSizing: 'border-box',
                          fontSize: '0.95rem',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#f97316';
                          e.target.style.background = 'white';
                          e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.background = '#fafafa';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        right: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '0.8rem',
                        color: '#9ca3af',
                        pointerEvents: 'none'
                      }}>
                        م²
                      </span>
                    </div>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '8px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500', 
                      color: '#6b7280'
                    }}>
                      <span style={{ fontSize: '14px' }}>💰</span>
                      {language === 'ar' ? 'الإيجار الشهري' : 'Monthly Rent'}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        value={formData.monthly_rent}
                        onChange={(e) => setFormData({...formData, monthly_rent: e.target.value})}
                        placeholder="5000"
                        min="0"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          paddingRight: '50px',
                          borderRadius: '12px',
                          border: '2px solid #e5e7eb',
                          background: '#fafafa',
                          boxSizing: 'border-box',
                          fontSize: '0.95rem',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#f97316';
                          e.target.style.background = 'white';
                          e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.background = '#fafafa';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        right: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '0.8rem',
                        color: '#9ca3af',
                        pointerEvents: 'none'
                      }}>
                        {language === 'ar' ? 'ر.س' : 'SAR'}
                      </span>
                    </div>
                    {formData.monthly_rent > 0 && formData.area_sqm > 0 && (
                      <div style={{
                        marginTop: '6px',
                        fontSize: '0.7rem',
                        color: '#9ca3af',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        💡 {(formData.monthly_rent / formData.area_sqm).toFixed(0)} {language === 'ar' ? 'ر.س/م²' : 'SAR/sqm'}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: '0' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontSize: '0.8rem', 
                    fontWeight: '500', 
                    color: '#6b7280'
                  }}>
                    {language === 'ar' ? 'الحالة' : 'Status'}
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      background: '#fafafa',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f97316';
                      e.target.style.background = 'white';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.background = '#fafafa';
                    }}
                  >
                    {Object.entries(statusMeta).map(([key, meta]) => (
                      <option key={key} value={key}>{meta.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Location / Google Maps */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '10px'
                }}>
                  {language === 'ar' ? 'الموقع' : 'Location'}
                </div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                  {language === 'ar' ? 'رابط خرائط جوجل' : 'Google Maps link'}
                </label>
                <input
                  type="url"
                  value={formData.location_url}
                  onChange={(e) => setFormData({ ...formData, location_url: e.target.value })}
                  onBlur={(e) => applyMapsLink(e.target.value)}
                  placeholder="https://maps.google.com/..."
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    boxSizing: 'border-box',
                    marginBottom: '10px',
                  }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <input
                    type="text"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    placeholder={language === 'ar' ? 'خط العرض' : 'Latitude'}
                    style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #e5e7eb' }}
                  />
                  <input
                    type="text"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    placeholder={language === 'ar' ? 'خط الطول' : 'Longitude'}
                    style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #e5e7eb' }}
                  />
                </div>
                {formData.latitude && formData.longitude && (
                  <a
                    href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-block', marginTop: 8, fontSize: '0.85rem', color: '#ea580c' }}
                  >
                    {language === 'ar' ? 'فتح على الخريطة' : 'Open on map'}
                  </a>
                )}
              </div>

              {/* Additional Notes Section */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ 
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '16px'
                }}>
                  {language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (Optional)'}
                </div>

                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  placeholder={language === 'ar' ? 'أي ملاحظات أو تفاصيل إضافية...' : 'Any additional notes or details...'}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    background: '#fafafa',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    lineHeight: '1.6',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#f97316';
                    e.target.style.background = 'white';
                    e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.background = '#fafafa';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Submit Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '12px',
                paddingTop: '24px',
                borderTop: '1px solid #f3f4f6'
              }}>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingUnit(null); }}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: '#f9fafb',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1.5,
                    padding: '16px',
                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
                  }}
                >
                  {editingUnit 
                    ? (
                      <>
                        <FaCheckCircle style={{ fontSize: '14px' }} />
                        {language === 'ar' ? 'حفظ التعديلات' : 'Save Changes'}
                      </>
                    )
                    : (
                      <>
                        <FaPlus style={{ fontSize: '14px' }} />
                        {language === 'ar' ? 'إضافة الوحدة' : 'Add Unit'}
                      </>
                    )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Listing Modal */}
      {showListingModal && listingUnit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaBullhorn style={{ color: '#f97316' }} />
                {language === 'ar' ? 'إنشاء إعلان' : 'Create Listing'}
              </h3>
              <button
                onClick={() => setShowListingModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ 
              padding: '12px', 
              backgroundColor: '#f3f4f6', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <strong>{language === 'ar' ? 'الوحدة:' : 'Unit:'}</strong> {listingUnit.unit_number}
              {listingUnit.monthly_rent && (
                <span style={{ marginRight: '10px', marginLeft: '10px' }}>
                  | <strong>{language === 'ar' ? 'الإيجار:' : 'Rent:'}</strong> {Number(listingUnit.monthly_rent).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
                </span>
              )}
            </div>
            
            <form onSubmit={handleListingSubmit}>
              <div style={{ display: 'grid', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'عنوان الإعلان *' : 'Listing Title *'}
                  </label>
                  <input
                    type="text"
                    value={listingFormData.title}
                    onChange={(e) => setListingFormData({...listingFormData, title: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'المنصة' : 'Platform'}
                  </label>
                  <select
                    value={listingFormData.platform}
                    onChange={(e) => setListingFormData({...listingFormData, platform: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    {platforms.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'السعر المعلن (ر.س)' : 'Listed Price (SAR)'}
                  </label>
                  <input
                    type="number"
                    value={listingFormData.price}
                    onChange={(e) => setListingFormData({...listingFormData, price: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                    placeholder={listingUnit.monthly_rent || ''}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'رقم ترخيص الإعلان (هيئة العقار)' : 'REGA ad license #'}
                  </label>
                  <input
                    type="text"
                    value={listingFormData.ad_license || ''}
                    onChange={(e) => setListingFormData({...listingFormData, ad_license: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                    placeholder={language === 'ar' ? 'مثال: 7201009770' : 'e.g. 7201009770'}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'رقم الإعلان على المنصة *' : 'Platform ad number *'}
                  </label>
                  <input
                    type="text"
                    value={listingFormData.platform_ad_number || ''}
                    onChange={(e) => setListingFormData({...listingFormData, platform_ad_number: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                    placeholder={language === 'ar' ? 'يميّز هذا الإعلان عن عقار/ديل/إنستغرام…' : 'Distinguishes this Aqar/Deal/IG post…'}
                  />
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {language === 'ar'
                      ? 'نفس الوحدة يمكن أن يكون لها عدة إعلانات — منصة مختلفة + رقم إعلان مختلف.'
                      : 'Same unit can have many ads — different platform + different ad number.'}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'رابط الإعلان على المنصة' : 'Platform ad URL'}
                  </label>
                  <input
                    type="url"
                    value={listingFormData.external_url || ''}
                    onChange={(e) => setListingFormData({...listingFormData, external_url: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'مهلة نشر الإعلان (ساعات)' : 'SLA Deadline (hours)'}
                  </label>
                  <select
                    value={listingFormData.sla_hours}
                    onChange={(e) => setListingFormData({...listingFormData, sla_hours: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    <option value={12}>12 {language === 'ar' ? 'ساعة' : 'hours'}</option>
                    <option value={24}>24 {language === 'ar' ? 'ساعة' : 'hours'}</option>
                    <option value={48}>48 {language === 'ar' ? 'ساعة' : 'hours'}</option>
                  </select>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    {language === 'ar' ? 'سيتم إنشاء تذكير إذا لم يُنشر الإعلان خلال هذه المدة' : 'A reminder will be created if ad not posted within this time'}
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginTop: '25px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowListingModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f97316',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <FaBullhorn />
                  {language === 'ar' ? 'إنشاء الإعلان' : 'Create Listing'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && depositUnit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaMoneyBillWave style={{ color: '#2563eb' }} />
                {language === 'ar' ? 'تسجيل عربون' : 'Record Deposit'}
              </h3>
              <button
                onClick={() => setShowDepositModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ 
              padding: '12px', 
              backgroundColor: '#dbeafe', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontSize: '14px',
              color: '#1e40af'
            }}>
              <strong>{language === 'ar' ? 'الوحدة:' : 'Unit:'}</strong> {depositUnit.unit_number}
              <div style={{ fontSize: '12px', marginTop: '4px', color: '#3b82f6' }}>
                {language === 'ar' 
                  ? 'سيتم تحويل حالة الوحدة إلى "محجوز" وإنشاء تذكير لإزالة الإعلان'
                  : 'Unit will be marked as "Reserved" and reminder created to remove ad'}
              </div>
            </div>
            
            <form onSubmit={handleDepositSubmit}>
              <div style={{ display: 'grid', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'اسم العميل *' : 'Client Name *'}
                  </label>
                  <input
                    type="text"
                    value={depositFormData.client_name}
                    onChange={(e) => setDepositFormData({...depositFormData, client_name: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                    placeholder={language === 'ar' ? 'مثال: أحمد محمد' : 'e.g. Ahmed Mohammed'}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'رقم الهاتف *' : 'Phone Number *'}
                  </label>
                  <input
                    type="tel"
                    value={depositFormData.client_phone}
                    onChange={(e) => setDepositFormData({...depositFormData, client_phone: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      boxSizing: 'border-box'
                    }}
                    placeholder="05xxxxxxxx"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'المبلغ (ر.س) *' : 'Amount (SAR) *'}
                    </label>
                    <input
                      type="number"
                      value={depositFormData.amount}
                      onChange={(e) => setDepositFormData({...depositFormData, amount: e.target.value})}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        boxSizing: 'border-box'
                      }}
                      placeholder="2500"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}
                    </label>
                    <select
                      value={depositFormData.payment_method}
                      onChange={(e) => setDepositFormData({...depositFormData, payment_method: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db'
                      }}
                    >
                      {paymentMethods.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Receipt Upload - Mandatory */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'صورة الإيصال *' : 'Receipt Image *'}
                  </label>
                  <div style={{ 
                    border: `2px dashed ${depositFormData.receipt_preview ? '#16a34a' : '#d1d5db'}`,
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: depositFormData.receipt_preview ? '#f0fdf4' : '#f9fafb',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => document.getElementById('receipt-input').click()}
                  >
                    <input
                      id="receipt-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleReceiptImageChange}
                      style={{ display: 'none' }}
                    />
                    
                    {depositFormData.receipt_preview ? (
                      <div>
                        <img 
                          src={depositFormData.receipt_preview} 
                          alt="Receipt preview"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '150px', 
                            borderRadius: '8px',
                            marginBottom: '10px'
                          }}
                        />
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: '6px',
                          color: '#16a34a',
                          fontSize: '14px'
                        }}>
                          <FaCheck />
                          {language === 'ar' ? 'تم إرفاق الإيصال' : 'Receipt attached'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                          {language === 'ar' ? 'اضغط لتغيير الصورة' : 'Click to change image'}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <FaCamera style={{ fontSize: '32px', color: '#9ca3af', marginBottom: '10px' }} />
                        <div style={{ color: '#374151', fontWeight: '500', marginBottom: '4px' }}>
                          {language === 'ar' ? 'اضغط لإرفاق صورة الإيصال' : 'Click to attach receipt image'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {language === 'ar' ? 'أو التقط صورة بالكاميرا' : 'or take a photo'}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                    {language === 'ar' 
                      ? 'بدلاً من إرسال الإيصال في جروب الواتساب، أرفقه هنا'
                      : 'Instead of sending to WhatsApp group, attach it here'}
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginTop: '25px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowDepositModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={uploadingReceipt || !depositFormData.receipt_preview}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: depositFormData.receipt_preview ? '#2563eb' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: depositFormData.receipt_preview ? 'pointer' : 'not-allowed',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: uploadingReceipt ? 0.7 : 1
                  }}
                >
                  <FaMoneyBillWave />
                  {uploadingReceipt 
                    ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                    : (language === 'ar' ? 'تسجيل العربون' : 'Record Deposit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && assignUnit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaBullhorn style={{ color: '#d97706' }} />
                {language === 'ar' ? 'تعيين مهمة تسويق' : 'Assign Marketing Task'}
              </h3>
              <button
                onClick={() => setShowAssignModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Unit Info */}
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              <strong>{language === 'ar' ? 'الوحدة:' : 'Unit:'}</strong> {assignUnit.unit_number}
              {assignUnit.monthly_rent && (
                <span style={{ marginRight: '10px', marginLeft: '10px' }}>
                  | <strong>{language === 'ar' ? 'الإيجار:' : 'Rent:'}</strong> {Number(assignUnit.monthly_rent).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
                </span>
              )}
            </div>
            
            <form onSubmit={handleAssignSubmit}>
              <div style={{ display: 'grid', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'تعيين إلى *' : 'Assign To *'}
                  </label>
                  {marketingTeam.length === 0 ? (
                    <div style={{
                      padding: '15px',
                      backgroundColor: '#fef2f2',
                      borderRadius: '8px',
                      color: '#dc2626',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}>
                      {language === 'ar' 
                        ? 'لا يوجد موظفين في قسم التسويق. يرجى إضافة موظف مع قسم "التسويق" أولاً.'
                        : 'No marketing team members. Please add an employee with "Marketing" department first.'}
                    </div>
                  ) : (
                    <select
                      value={selectedEmployee}
                      onChange={(e) => setSelectedEmployee(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db'
                      }}
                    >
                      <option value="">{language === 'ar' ? 'اختر موظف...' : 'Select employee...'}</option>
                      {marketingTeam.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'المهلة (SLA)' : 'Deadline (SLA)'}
                  </label>
                  <select
                    value={assigningSLA}
                    onChange={(e) => setAssigningSLA(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    <option value={1}>1 {language === 'ar' ? 'ساعة' : 'hour'} ({language === 'ar' ? 'افتراضي' : 'default'})</option>
                    <option value={4}>4 {language === 'ar' ? 'ساعات' : 'hours'}</option>
                    <option value={8}>8 {language === 'ar' ? 'ساعات' : 'hours'}</option>
                    <option value={12}>12 {language === 'ar' ? 'ساعة' : 'hours'}</option>
                    <option value={24}>24 {language === 'ar' ? 'ساعة' : 'hours'}</option>
                    <option value={48}>48 {language === 'ar' ? 'ساعة' : 'hours'}</option>
                  </select>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    {language === 'ar' 
                      ? 'الموظف سيحصل على تذكير قبل انتهاء المهلة'
                      : 'Employee will get a reminder before deadline'}
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginTop: '25px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submittingAssignment || !selectedEmployee || marketingTeam.length === 0}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: marketingTeam.length > 0 && selectedEmployee ? '#d97706' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: marketingTeam.length > 0 && selectedEmployee ? 'pointer' : 'not-allowed',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: submittingAssignment ? 0.7 : 1
                  }}
                >
                  <FaBullhorn />
                  {submittingAssignment 
                    ? (language === 'ar' ? 'جاري التعيين...' : 'Assigning...')
                    : (language === 'ar' ? 'تعيين المهمة' : 'Assign Task')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Deposit Request Modal (Step 1) */}
      {showCancelDepositModal && cancellingDeposit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaExclamationTriangle />
                {language === 'ar' ? 'طلب إلغاء العربون' : 'Request Deposit Cancellation'}
              </h3>
              <button
                onClick={() => setShowCancelDepositModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Warning */}
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#fef2f2', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #fca5a5'
            }}>
              <div style={{ fontWeight: '600', color: '#dc2626', marginBottom: '8px' }}>
                {language === 'ar' ? 'تحذير: سيتم:' : 'Warning: This will:'}
              </div>
              <ul style={{ margin: 0, paddingRight: language === 'ar' ? '20px' : '0', paddingLeft: language === 'ar' ? '0' : '20px', color: '#7f1d1d', fontSize: '14px' }}>
                <li>{language === 'ar' ? 'إلغاء العربون وتغيير حالته' : 'Cancel the deposit'}</li>
                <li>{language === 'ar' ? 'إرجاع حالة الوحدة إلى "شاغرة"' : 'Revert unit status to "vacant"'}</li>
                <li>{language === 'ar' ? 'إلغاء تذكيرات إغلاق الإعلان' : 'Cancel ad closure reminders'}</li>
                <li>{language === 'ar' ? 'إنشاء مهمة إعادة إعلان (إذا تم إغلاق الإعلان)' : 'Create re-advertise task (if ad was closed)'}</li>
              </ul>
            </div>

            {/* Deposit Info */}
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#f3f4f6', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <div style={{ marginBottom: '5px' }}>
                <strong>{language === 'ar' ? 'الوحدة:' : 'Unit:'}</strong> {cancellingDeposit.unit_number}
              </div>
              <div style={{ marginBottom: '5px' }}>
                <strong>{language === 'ar' ? 'المبلغ:' : 'Amount:'}</strong> {Number(cancellingDeposit.amount).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
              </div>
              <div>
                <strong>{language === 'ar' ? 'العميل:' : 'Client:'}</strong> {cancellingDeposit.client_name}
              </div>
            </div>
            
            <form onSubmit={handleRequestCancellation}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                  {language === 'ar' ? 'سبب الإلغاء * (مطلوب)' : 'Cancellation Reason * (required)'}
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  required
                  minLength={10}
                  rows={4}
                  placeholder={language === 'ar' 
                    ? 'اكتب سبب إلغاء العربون بالتفصيل (10 أحرف على الأقل)...'
                    : 'Enter detailed reason for cancellation (minimum 10 characters)...'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    resize: 'vertical'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>
                  {cancellationReason.length}/10 {language === 'ar' ? 'أحرف' : 'characters'}
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowCancelDepositModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {language === 'ar' ? 'تراجع' : 'Back'}
                </button>
                <button
                  type="submit"
                  disabled={submittingCancellation || cancellationReason.length < 10}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: cancellationReason.length >= 10 ? '#dc2626' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: cancellationReason.length >= 10 ? 'pointer' : 'not-allowed',
                    fontWeight: '500',
                    opacity: submittingCancellation ? 0.7 : 1
                  }}
                >
                  {submittingCancellation 
                    ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...')
                    : (language === 'ar' ? 'إرسال طلب الإلغاء' : 'Submit Cancellation Request')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approve Cancellation Modal (Step 2 - Owner/Manager) */}
      {showApproveModal && approvingDeposit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaCheckCircle />
                {language === 'ar' ? 'الموافقة على إلغاء العربون' : 'Approve Deposit Cancellation'}
              </h3>
              <button
                onClick={() => setShowApproveModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Deposit Info */}
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f3f4f6', 
              borderRadius: '8px', 
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              <div style={{ marginBottom: '5px' }}>
                <strong>{language === 'ar' ? 'الوحدة:' : 'Unit:'}</strong> {approvingDeposit.unit_number}
              </div>
              <div style={{ marginBottom: '5px' }}>
                <strong>{language === 'ar' ? 'المبلغ:' : 'Amount:'}</strong> {Number(approvingDeposit.amount).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
              </div>
              <div style={{ marginBottom: '5px' }}>
                <strong>{language === 'ar' ? 'العميل:' : 'Client:'}</strong> {approvingDeposit.client_name}
              </div>
              <div style={{ marginBottom: '5px' }}>
                <strong>{language === 'ar' ? 'طالب الإلغاء:' : 'Requested by:'}</strong> {approvingDeposit.requested_by_name}
              </div>
              <div style={{ 
                marginTop: '10px', 
                padding: '10px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '6px',
                color: '#92400e'
              }}>
                <strong>{language === 'ar' ? 'سبب الإلغاء:' : 'Reason:'}</strong><br />
                {approvingDeposit.cancellation_reason}
              </div>
            </div>
            
            <form onSubmit={handleApproveCancellation}>
              <div style={{ display: 'grid', gap: '15px', marginBottom: '20px' }}>
                {/* Refund Status */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                    {language === 'ar' ? 'حالة الاسترداد' : 'Refund Status'}
                  </label>
                  <select
                    value={refundData.refund_status}
                    onChange={(e) => setRefundData({ ...refundData, refund_status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    }}
                  >
                    <option value="full">{language === 'ar' ? 'استرداد كامل' : 'Full Refund'}</option>
                    <option value="partial">{language === 'ar' ? 'استرداد جزئي' : 'Partial Refund'}</option>
                    <option value="none">{language === 'ar' ? 'بدون استرداد' : 'No Refund'}</option>
                    <option value="pending">{language === 'ar' ? 'معلق (سيتم لاحقاً)' : 'Pending (later)'}</option>
                  </select>
                </div>

                {/* Refund Amount (if partial) */}
                {refundData.refund_status === 'partial' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'مبلغ الاسترداد' : 'Refund Amount'}
                    </label>
                    <input
                      type="number"
                      value={refundData.refund_amount}
                      onChange={(e) => setRefundData({ ...refundData, refund_amount: e.target.value })}
                      max={approvingDeposit.amount}
                      min={0}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db'
                      }}
                    />
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>
                      {language === 'ar' ? `الحد الأقصى: ${approvingDeposit.amount} ر.س` : `Max: ${approvingDeposit.amount} SAR`}
                    </div>
                  </div>
                )}

                {/* Refund Method */}
                {refundData.refund_status !== 'none' && refundData.refund_status !== 'pending' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'طريقة الاسترداد' : 'Refund Method'}
                    </label>
                    <select
                      value={refundData.refund_method}
                      onChange={(e) => setRefundData({ ...refundData, refund_method: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db'
                      }}
                    >
                      <option value="transfer">{language === 'ar' ? 'تحويل بنكي' : 'Bank Transfer'}</option>
                      <option value="cash">{language === 'ar' ? 'نقداً' : 'Cash'}</option>
                      <option value="check">{language === 'ar' ? 'شيك' : 'Check'}</option>
                    </select>
                  </div>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowApproveModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {language === 'ar' ? 'تراجع' : 'Back'}
                </button>
                <button
                  type="submit"
                  disabled={submittingCancellation}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: submittingCancellation ? 0.7 : 1
                  }}
                >
                  <FaCheck />
                  {submittingCancellation 
                    ? (language === 'ar' ? 'جاري الموافقة...' : 'Approving...')
                    : (language === 'ar' ? 'الموافقة وإلغاء العربون' : 'Approve & Cancel Deposit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Deposit Modal */}
      {showViewDepositModal && viewingDeposit && (
        <div className="modal-overlay" onClick={() => setShowViewDepositModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{language === 'ar' ? 'تفاصيل العربون' : 'Deposit Details'}</h2>
              <button className="close-btn" onClick={() => setShowViewDepositModal(false)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                  <strong>{language === 'ar' ? 'الوحدة:' : 'Unit:'}</strong> {viewingDeposit.unit_number || 'N/A'}
                </div>
                <div>
                  <strong>{language === 'ar' ? 'العميل:' : 'Client:'}</strong> {viewingDeposit.client_name || 'N/A'}
                </div>
                <div>
                  <strong>{language === 'ar' ? 'رقم الهاتف:' : 'Phone:'}</strong> {viewingDeposit.client_phone || 'N/A'}
                </div>
                <div>
                  <strong>{language === 'ar' ? 'المبلغ:' : 'Amount:'}</strong> {Number(viewingDeposit.amount || 0).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
                </div>
                <div>
                  <strong>{language === 'ar' ? 'طريقة الدفع:' : 'Payment Method:'}</strong> {viewingDeposit.payment_method || 'N/A'}
                </div>
                <div>
                  <strong>{language === 'ar' ? 'الحالة:' : 'Status:'}</strong> 
                  <span style={{ 
                    marginLeft: '8px',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    backgroundColor: viewingDeposit.status === 'confirmed' ? '#d1fae5' : 
                                    viewingDeposit.status === 'cancelled' ? '#fee2e2' : '#fef3c7',
                    color: viewingDeposit.status === 'confirmed' ? '#065f46' : 
                           viewingDeposit.status === 'cancelled' ? '#991b1b' : '#92400e',
                    fontWeight: '600'
                  }}>
                    {viewingDeposit.status === 'confirmed' ? (language === 'ar' ? 'مؤكد' : 'Confirmed') :
                     viewingDeposit.status === 'cancelled' ? (language === 'ar' ? 'ملغي' : 'Cancelled') :
                     (language === 'ar' ? 'قيد المراجعة' : 'Pending')}
                  </span>
                </div>
                {viewingDeposit.created_at && (
                  <div>
                    <strong>{language === 'ar' ? 'تاريخ التسجيل:' : 'Date:'}</strong> {new Date(viewingDeposit.created_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                  </div>
                )}
                {viewingDeposit.receipt_image && (
                  <div>
                    <strong>{language === 'ar' ? 'إيصال الدفع:' : 'Receipt:'}</strong>
                    <div style={{ marginTop: '10px' }}>
                      <img 
                        src={viewingDeposit.receipt_image} 
                        alt="Receipt" 
                        style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                    </div>
                  </div>
                )}
                {viewingDeposit.status === 'cancelled' && viewingDeposit.refund_status && (
                  <div style={{ padding: '15px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                    <strong>{language === 'ar' ? 'معلومات الاسترداد:' : 'Refund Info:'}</strong>
                    <div style={{ marginTop: '8px' }}>
                      <div><strong>{language === 'ar' ? 'حالة الاسترداد:' : 'Refund Status:'}</strong> {viewingDeposit.refund_status}</div>
                      {viewingDeposit.refund_amount && (
                        <div><strong>{language === 'ar' ? 'المبلغ المسترد:' : 'Refund Amount:'}</strong> {Number(viewingDeposit.refund_amount).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}</div>
                      )}
                      {viewingDeposit.refund_method && (
                        <div><strong>{language === 'ar' ? 'طريقة الاسترداد:' : 'Refund Method:'}</strong> {viewingDeposit.refund_method}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowViewDepositModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {language === 'ar' ? 'إغلاق' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Cancellations Alert (for Owner/Manager) */}
      {canApprove && pendingCancellations.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#fef2f2',
          border: '2px solid #dc2626',
          borderRadius: '12px',
          padding: '15px 25px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <FaExclamationTriangle style={{ color: '#dc2626', fontSize: '24px' }} />
          <div>
            <div style={{ fontWeight: '600', color: '#dc2626' }}>
              {language === 'ar' 
                ? `${pendingCancellations.length} طلب إلغاء عربون بانتظار الموافقة`
                : `${pendingCancellations.length} deposit cancellation(s) pending approval`}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
              {pendingCancellations.map(dep => (
                <button
                  key={dep.id}
                  onClick={() => openApproveModal(dep)}
                  style={{
                    padding: '5px 12px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {dep.unit_number} - {Number(dep.amount).toLocaleString()} {language === 'ar' ? 'ر.س' : 'SAR'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* View Listing Details Modal */}
      {showViewListingModal && viewingListing && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowViewListingModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '30px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, color: '#1f2937' }}>
                {language === 'ar' ? 'تفاصيل الإعلان' : 'Listing Details'}
              </h2>
              <button
                onClick={() => setShowViewListingModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '20px' }}>
              {/* Listing Info */}
              <div>
                <h3 style={{ margin: '0 0 15px 0', color: '#374151', fontSize: '1.1rem' }}>
                  {language === 'ar' ? 'معلومات الإعلان' : 'Listing Information'}
                </h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                      {language === 'ar' ? 'العنوان' : 'Title'}
                    </label>
                    <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                      {viewingListing.title || '-'}
                    </div>
                  </div>
                  
                  {viewingListing.description && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'الوصف' : 'Description'}
                      </label>
                      <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                        {viewingListing.description}
                      </div>
                    </div>
                  )}
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'المنصة' : 'Platform'}
                      </label>
                      <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                        {viewingListing.platform || '-'}
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'السعر' : 'Price'}
                      </label>
                      <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                        {viewingListing.price ? `${Number(viewingListing.price).toLocaleString()} ${language === 'ar' ? 'ر.س' : 'SAR'}` : '-'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'رقم ترخيص الإعلان (هيئة العقار)' : 'REGA ad license #'}
                      </label>
                      <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                        {viewingListing.ad_license || '-'}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'رقم الإعلان على المنصة' : 'Platform ad number'}
                      </label>
                      <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                        {viewingListing.platform_ad_number || viewingListing.external_id || '-'}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'الحالة' : 'Status'}
                      </label>
                      <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                        {viewingListing.status === 'ad_posted' ? (language === 'ar' ? 'تم النشر' : 'Posted') :
                         viewingListing.status === 'active' ? (language === 'ar' ? 'نشط' : 'Active') :
                         viewingListing.status === 'pending' ? (language === 'ar' ? 'قيد الانتظار' : 'Pending') :
                         viewingListing.status || '-'}
                      </div>
                    </div>
                    
                    {viewingListing.posted_at && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', color: '#6b7280', fontSize: '0.9rem' }}>
                          {language === 'ar' ? 'تاريخ النشر' : 'Posted At'}
                        </label>
                        <div style={{ padding: '10px', background: '#f9fafb', borderRadius: '8px', color: '#1f2937' }}>
                          {new Date(viewingListing.posted_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* External URL Section */}
              <div>
                <h3 style={{ margin: '0 0 15px 0', color: '#374151', fontSize: '1.1rem' }}>
                  {language === 'ar' ? 'رابط الإعلان' : 'Ad URL'}
                </h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <input
                    type="url"
                    value={editingListingUrl}
                    onChange={(e) => setEditingListingUrl(e.target.value)}
                    placeholder={language === 'ar' ? 'https://haraj.com.sa/...' : 'https://haraj.com.sa/...'}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.95rem'
                    }}
                  />
                  <button
                    onClick={handleSaveListingUrl}
                    disabled={savingListingUrl}
                    style={{
                      padding: '12px 24px',
                      background: savingListingUrl ? '#9ca3af' : '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: savingListingUrl ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.95rem'
                    }}
                  >
                    {savingListingUrl 
                      ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                      : (language === 'ar' ? 'حفظ' : 'Save')
                    }
                  </button>
                </div>
                
                {viewingListing.external_url && viewingListing.external_url.trim() && (
                  <div style={{ marginTop: '15px' }}>
                    <a
                      href={viewingListing.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        background: '#dbeafe',
                        color: '#2563eb',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '0.95rem'
                      }}
                    >
                      <FaExternalLinkAlt />
                      {language === 'ar' ? 'فتح الإعلان في نافذة جديدة' : 'Open Ad in New Tab'}
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowViewListingModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Animation */}
      <SuccessAnimation 
        show={showSuccess} 
        onComplete={() => setShowSuccess(false)}
        message={successMessage}
      />

      {showInventoryWizard && (
        <InventoryWizard
          language={language}
          onClose={() => setShowInventoryWizard(false)}
          onSuccess={(result) => {
            setShowInventoryWizard(false);
            loadUnits();
            loadProperties();
            loadProjects();
            if (result?.projectId) {
              setMainView('projects');
            }
            setSuccessMessage(
              language === 'ar'
                ? `تمت إضافة ${result?.unitCount || 1} وحدة بنجاح`
                : `Added ${result?.unitCount || 1} unit(s) successfully`
            );
            setShowSuccess(true);
          }}
        />
      )}
    </div>
  );
};

export default Units;
